<template>
  <div class="col-12">
    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100 d-flex justify-content-between align-items-center">
          <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
            {{ t('attendance.edit_attendance_title') }}
          </h6>
        </div>
      </div>

      <LoadingSpinner v-if="isLoading" :message="t('attendance.loading_attendance')" />

      <div v-else class="card-body px-0 pb-2 pe-3">
        <div class="row justify-content-center">
          <div class="col-md-8 col-lg-6">
            <div class="p-4">
              <div class="mb-4">
                <label class="form-label">{{ t('attendance.student_name') }}</label>
                <input 
                  type="text" 
                  class="form-control borderless-input" 
                  :value="studentFullName" 
                  disabled
                >
              </div>
              
              <div class="mb-4">
                <label class="form-label">{{ t('attendance.date') }}</label>
                <input 
                  type="text" 
                  class="form-control borderless-input" 
                  :value="formatDate(attendance.date)" 
                  disabled
                >
              </div>
              
              <div class="mb-4">
                <label class="form-label">{{ t('attendance.status') }}</label>
                <select 
                  class="form-select borderless-input" 
                  v-model="status"
                  :disabled="isSaving"
                >
                  <option value="present">{{ t('attendance.present') }}</option>
                  <option value="absent">{{ t('attendance.absent') }}</option>
                  <option value="late">{{ t('attendance.late') }}</option>
                  <option value="excused">{{ t('attendance.excused') }}</option>
                </select>
              </div>
              
              <div class="mb-4">
                <label class="form-label">{{ t('attendance.notes') }}</label>
                <textarea 
                  class="form-control borderless-input" 
                  v-model="notes"
                  rows="3"
                  :disabled="isSaving"
                ></textarea>
              </div>
              
              <div class="mt-4 d-flex gap-2">
                <button 
                  @click="saveChanges"
                  class="btn btn-primary btn-sm flex-grow-1"
                  :disabled="isSaving || !hasChanges"
                >
                  <span v-if="isSaving">
                    <i class="fas fa-spinner fa-spin me-2"></i>
                    {{ t('attendance.saving') }}
                  </span>
                  <span v-else>
                    <i class="fas fa-edit me-2"></i>
                    {{ t('attendance.save_changes') }}
                  </span>
                </button>
                <button
                  @click="goBack"
                  class="btn btn-outline-secondary btn-sm flex-grow-1"
                  :disabled="isSaving"
                >
                  <i class="fas fa-times me-2"></i>
                  {{ t('attendance.cancel') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import api from '@/api';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const toast = useToast();

const attendance = ref({
  id: null,
  student: { 
    first_name: '',
    father_name: '',
    last_name: '' 
  },
  date: '',
  status: '',
  notes: '',
  day: ''
});
const status = ref('');
const notes = ref('');
const isSaving = ref(false);
const isLoading = ref(true); 

const hasChanges = computed(() => {
  return status.value !== attendance.value.status || 
         notes.value !== (attendance.value.notes || '');
});

const studentFullName = computed(() => {
  if (attendance.value.student) {
    return `${attendance.value.student.first_name} ${attendance.value.student.father_name} ${attendance.value.student.last_name}`;
  }
  return t('attendance.unknown');
});

const formatDate = (dateString) => {
  if (!dateString) return t('attendance.not_specified');
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('ar-LY', options);
};

const fetchAttendance = async () => {
  try {
    const response = await api.get(`/attendances/${route.params.id}`, {
      params: {
        include: 'student'
      }
    });
    attendance.value = response.data.data;
    status.value = attendance.value.status;
    notes.value = attendance.value.notes || '';
  } catch (error) {
    console.error("Error fetching attendance:", error);
    toast.error(t('attendance.load_failed'));
    router.push('/attendances');
  } finally {
    isLoading.value = false;
  }
};

const saveChanges = async () => {
  if (!hasChanges.value) {
    toast.info(t('attendance.no_changes'));
    return;
  }

  isSaving.value = true;
  try {
    await api.put(`/attendances/${attendance.value.id}`, {
      status: status.value,
      notes: notes.value
    });
    
    toast.success(t('attendance.update_success'));
    goBack();
  } catch (error) {
    const errorMsg = error.response?.data?.message || 
                   error.response?.data?.error || 
                   t('attendance.update_failed');
    toast.error(errorMsg);
    console.error("Error details:", error.response?.data);
  } finally {
    isSaving.value = false;
  }
};

const goBack = () => {
  router.push('/attendances');
};

onMounted(() => {
  fetchAttendance();
});
</script>